import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import AdminNavigation from '../../components/ui/AdminNavigation';
import BreadcrumbTrail from '../../components/ui/BreadcrumbTrail';
import { containerService } from '../../services/containerService';
import { trackContainerEvent, trackFileUpload, trackButtonClick, trackError } from '../../services/analyticsService';

export default function AdminContainerDetail() {
  const { containerId: id } = useParams();
  const navigate = useNavigate();
  const [container, setContainer] = useState(null);
  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isEditingInfo, setIsEditingInfo] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [revenue, setRevenue] = useState(null);
  const [loadingRevenue, setLoadingRevenue] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [uploadingDocument, setUploadingDocument] = useState(false);
  const [newStatus, setNewStatus] = useState('');

  useEffect(() => {
    loadContainerData();
  }, [id]);

  const loadContainerData = async () => {
    try {
      setLoading(true);
      trackContainerEvent('viewed_admin', { id });
      
      const [containerData, shipmentsData] = await Promise.all([
        containerService?.getById(id),
        containerService?.getContainerShipments(id)
      ]);
      setContainer(containerData);
      setShipments(shipmentsData);
      setError('');
      
      loadRevenue();
      loadDocuments();
    } catch (err) {
      setError('Erreur lors du chargement des données du conteneur');
      console.error('Error loading container data:', err);
      trackError('container_load_error', err?.message, { container_id: id });
    } finally {
      setLoading(false);
    }
  };

  const loadRevenue = async () => {
    try {
      setLoadingRevenue(true);
      const revenueData = await containerService?.getContainerRevenue(id);
      setRevenue(revenueData);
    } catch (err) {
      console.error('Error loading revenue:', err);
    } finally {
      setLoadingRevenue(false);
    }
  };

  const loadDocuments = async () => {
    try {
      setLoadingDocuments(true);
      const docs = await containerService?.getContainerDocuments(id);
      setDocuments(docs);
    } catch (err) {
      console.error('Error loading documents:', err);
    } finally {
      setLoadingDocuments(false);
    }
  };

  const handleDocumentUpload = async (event) => {
    const file = event?.target?.files?.[0];
    if (!file) return;

    try {
      setUploadingDocument(true);
      trackFileUpload('container_document', file?.name, file?.size);
      
      await containerService?.uploadContainerDocument(id, file, 'manifest');
      alert('Document téléchargé avec succès');
      loadDocuments();
    } catch (err) {
      alert('Erreur lors du téléchargement du document');
      console.error('Error uploading document:', err);
      trackError('container_document_upload_error', err?.message, { container_id: id });
    } finally {
      setUploadingDocument(false);
      event.target.value = '';
    }
  };

  const handleDocumentDownload = async (filePath, fileName) => {
    try {
      trackButtonClick('download_container_document', { file_name: fileName });
      await containerService?.downloadContainerDocument(filePath, fileName);
    } catch (err) {
      alert('Erreur lors du téléchargement du document');
      console.error('Error downloading document:', err);
      trackError('container_document_download_error', err?.message, { container_id: id });
    }
  };

  const handleDocumentDelete = async (filePath) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce document ?')) return;

    try {
      await containerService?.deleteContainerDocument(filePath);
      alert('Document supprimé avec succès');
      loadDocuments();
    } catch (err) {
      alert('Erreur lors de la suppression du document');
      console.error('Error deleting document:', err);
    }
  };

  const handleExportManifest = async () => {
    try {
      setIsExporting(true);
      const { container: containerInfo, shipments: shipmentsData } = await containerService?.generateManifestData(id);
      containerService?.downloadManifestPDF(containerInfo, shipmentsData);
    } catch (err) {
      alert('Erreur lors de l\'export du manifeste');
      console.error('Error exporting manifest:', err);
    } finally {
      setIsExporting(false);
    }
  };

  const handleCloseContainer = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir fermer ce conteneur ?')) return;

    try {
      await containerService?.updateStatus(id, 'closed');
      await loadContainerData();
      alert('Conteneur fermé avec succès');
    } catch (err) {
      alert('Erreur lors de la fermeture du conteneur');
      console.error('Error closing container:', err);
    }
  };

  const handleStatusChange = async (newStatus) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir changer le statut à "${getStatusLabel(newStatus)}" ?`)) return;

    try {
      // ✅ Capture containerCode BEFORE reload (React state is stale after async setState)
      const currentContainerCode = container?.containerCode;

      // ✅ CRITICAL: Update container status first - this is the DB operation
      await containerService?.updateStatus(id, newStatus);

      // ✅ Send N8N webhook BEFORE reloading data to avoid stale state issue
      try {
        const webhookResult = await containerService?.sendN8nWebhookToAllClients(id, {
          containerCode: currentContainerCode,
          status: newStatus,
          id: id
        });

        if (webhookResult?.success) {
          console.log(`Webhook notifications sent: ${webhookResult?.sent}/${webhookResult?.total} clients`);
          if (webhookResult?.failed > 0) {
            console.warn(`${webhookResult?.failed} webhook notifications failed`);
          }
        }
      } catch (webhookError) {
        // Webhook errors are logged but do not block the UI update
        console.error('Webhook notification error (non-blocking):', webhookError);
      }

      // ✅ Reload container data after webhook is sent
      await loadContainerData();

      alert('Statut du conteneur mis à jour avec succès');
    } catch (err) {
      alert('Erreur lors de la mise à jour du statut');
      console.error('Error updating container status:', err);
    }
  };

  const handleStatusUpdate = async (newStatus) => {
    try {
      trackContainerEvent('status_updated', {
        id,
        reference: container?.container_code,
        old_status: container?.status,
        new_status: newStatus
      });
      
      await containerService?.updateStatus(id, newStatus);
      await loadContainerData();
    } catch (err) {
      trackError('container_status_update_error', err?.message, { container_id: id });
    }
  };

  const getStatusLabel = (status) => {
    // ✅ FIX: Map NEW database enum values (uppercase) to French labels for display
    const statusLabels = {
      AU_PORT_LE_HAVRE_ANVERS: 'Au port Le Havre / Anvers',
      EN_TRANSIT_MARITIME: 'En transit maritime',
      ARRIVE_A_ABIDJAN: 'Arrivé au port d\'Abidjan',
      DEDOUANEMENT_EN_COURS: 'En phase de dédouanement',
      TRANSFERT_A_ENTREPOT: 'À l\'entrepôt à Abidjan',
      DISTRIBUTION_EN_COURS: 'Distribution des colis en cours',
      CONTENEUR_TERMINE: 'Conteneur terminé'
    };
    return statusLabels?.[status] || status;
  };

  const getStatusBadge = (status) => {
    // ✅ FIX: Use NEW uppercase enum values for display styling
    const statusConfig = {
      AU_PORT_LE_HAVRE_ANVERS: { label: 'Au port Le Havre / Anvers', color: 'bg-blue-100 text-blue-800' },
      EN_TRANSIT_MARITIME: { label: 'En transit maritime', color: 'bg-cyan-100 text-cyan-800' },
      ARRIVE_A_ABIDJAN: { label: 'Arrivé au port d\'Abidjan', color: 'bg-violet-100 text-violet-800' },
      DEDOUANEMENT_EN_COURS: { label: 'En phase de dédouanement', color: 'bg-orange-100 text-orange-800' },
      TRANSFERT_A_ENTREPOT: { label: 'À l\'entrepôt à Abidjan', color: 'bg-emerald-100 text-emerald-800' },
      DISTRIBUTION_EN_COURS: { label: 'Distribution des colis en cours', color: 'bg-teal-100 text-teal-800' },
      CONTENEUR_TERMINE: { label: 'Conteneur terminé', color: 'bg-green-600 text-white' }
    };

    const config = statusConfig?.[status] || { label: status, color: 'bg-gray-100 text-gray-800' };

    return (
      <span className={`px-3 py-1 text-sm font-medium rounded-full ${config?.color}`}>
        {config?.label}
      </span>
    );
  };

  const getShipmentStatusBadge = (status) => {
    const statusConfig = {
      pending: { label: 'En attente', color: 'bg-yellow-100 text-yellow-800' },
      in_transit: { label: 'En transit', color: 'bg-blue-100 text-blue-800' },
      customs: { label: 'En douane', color: 'bg-purple-100 text-purple-800' },
      out_for_delivery: { label: 'En livraison', color: 'bg-indigo-100 text-indigo-800' },
      delivered: { label: 'Livré', color: 'bg-green-100 text-green-800' },
      exception: { label: 'Exception', color: 'bg-red-100 text-red-800' }
    };

    const config = statusConfig?.[status] || { label: status, color: 'bg-gray-100 text-gray-800' };

    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${config?.color}`}>
        {config?.label}
      </span>
    );
  };

  // ✅ HELPER: Get payment status badge
  const getPaymentBadge = (paymentStatus) => {
    const badges = {
      'paye': { bg: 'bg-green-100', text: 'text-green-700', label: 'Payé', icon: '✓' },
      'partiel': { bg: 'bg-orange-100', text: 'text-orange-700', label: 'Partiel', icon: '◐' },
      'non_paye': { bg: 'bg-red-100', text: 'text-red-700', label: 'Non payé', icon: '✕' }
    };
    
    const badge = badges?.[paymentStatus] || badges?.['non_paye'];
    
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-semibold ${badge?.bg} ${badge?.text}`}>
        <span>{badge?.icon}</span>
        <span>{badge?.label}</span>
      </span>
    );
  };

  const breadcrumbItems = [
    { label: 'Tableau de bord', path: '/admin-shipments-dashboard' },
    { label: 'Conteneurs', path: '/admin-containers-dashboard' },
    { label: container?.containerCode || 'Détails', path: `/admin/containers/${id}` }
  ];

  // ✅ KPI Total colis : calculé depuis shipments (SUM shipment_items.quantity)
  const totalPackages = (shipments || [])?.reduce(
    (sum, shipment) => sum + (Number(shipment?.totalPackages) || 0),
    0
  );
  console.log('TOTAL PACKAGES KPI FINAL:', totalPackages);

  // ✅ FIX: Always render consistent DOM structure to prevent MutationObserver errors
  return (
    <div className="min-h-screen bg-gray-50 pb-8">
      <AdminNavigation onLogout={() => navigate('/admin-login')} />
      <BreadcrumbTrail customItems={breadcrumbItems} />
      
      {/* ✅ CRITICAL: Stable content wrapper that always exists */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" id="container-detail-content">
        {loading ? (
          <div className="bg-white rounded-lg shadow-sm p-8 text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
            <p className="mt-4 text-gray-600">Chargement...</p>
          </div>
        ) : error || !container ? (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800">{error || 'Conteneur non trouvé'}</p>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="mb-8">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Icon name="Package" size={24} className="text-blue-600" />
                  </div>
                  <div>
                    <h1 className="text-3xl font-bold text-gray-900">{container?.containerCode}</h1>
                    <p className="text-gray-600 mt-1">Détails du conteneur maritime</p>
                  </div>
                </div>
                <div className="flex space-x-3">
                  <button
                    onClick={handleExportManifest}
                    disabled={isExporting || shipments?.length === 0}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2 disabled:bg-gray-400 disabled:cursor-not-allowed"
                  >
                    <Icon name="Download" size={20} />
                    <span>{isExporting ? 'Export...' : 'Télécharger manifeste PDF'}</span>
                  </button>
                  {container?.status === 'open' && (
                    <button
                      onClick={handleCloseContainer}
                      className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors"
                    >
                      Fermer le conteneur
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Container Info */}
            <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-gray-900">Informations du conteneur</h2>
                {getStatusBadge(container?.status)}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Port de départ</label>
                  <p className="text-gray-900">{container?.departurePort || '-'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Port de destination</label>
                  <p className="text-gray-900">{container?.destinationPort || '-'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date de départ</label>
                  <p className="text-gray-900">
                    {container?.departureDate ? new Date(container.departureDate)?.toLocaleDateString('fr-FR') : '-'}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date d'arrivée estimée</label>
                  <p className="text-gray-900">
                    {container?.etaDate ? new Date(container.etaDate)?.toLocaleDateString('fr-FR') : '-'}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Compagnie maritime</label>
                  <p className="text-gray-900">{container?.shippingLine || '-'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nom du navire</label>
                  <p className="text-gray-900">{container?.vesselName || '-'}</p>
                </div>
              </div>

              {container?.notes && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                  <p className="text-gray-900">{container?.notes}</p>
                </div>
              )}
            </div>

            {/* ✅ FIX: Status Management Section with CORRECT uppercase enum values */}
            <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-6">Gestion du statut</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                <button
                  onClick={() => handleStatusChange('AU_PORT_LE_HAVRE_ANVERS')}
                  disabled={container?.status === 'AU_PORT_LE_HAVRE_ANVERS'}
                  className={`px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                    container?.status === 'AU_PORT_LE_HAVRE_ANVERS' ?'bg-blue-100 text-blue-800 cursor-not-allowed' :'bg-white border-2 border-blue-600 text-blue-600 hover:bg-blue-50'
                  }`}
                >
                  <Icon name="Anchor" size={20} className="mx-auto mb-1" />
                  <span className="block text-xs">Au port Le Havre/Anvers</span>
                </button>

                <button
                  onClick={() => handleStatusChange('EN_TRANSIT_MARITIME')}
                  disabled={container?.status === 'EN_TRANSIT_MARITIME'}
                  className={`px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                    container?.status === 'EN_TRANSIT_MARITIME' ?'bg-cyan-100 text-cyan-800 cursor-not-allowed' :'bg-white border-2 border-cyan-600 text-cyan-600 hover:bg-cyan-50'
                  }`}
                >
                  <Icon name="Ship" size={20} className="mx-auto mb-1" />
                  <span className="block text-xs">En transit maritime</span>
                </button>

                <button
                  onClick={() => handleStatusChange('ARRIVE_A_ABIDJAN')}
                  disabled={container?.status === 'ARRIVE_A_ABIDJAN'}
                  className={`px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                    container?.status === 'ARRIVE_A_ABIDJAN' ?'bg-violet-100 text-violet-800 cursor-not-allowed' :'bg-white border-2 border-violet-600 text-violet-600 hover:bg-violet-50'
                  }`}
                >
                  <Icon name="MapPin" size={20} className="mx-auto mb-1" />
                  <span className="block text-xs">Arrivé au port d'Abidjan</span>
                </button>

                <button
                  onClick={() => handleStatusChange('DEDOUANEMENT_EN_COURS')}
                  disabled={container?.status === 'DEDOUANEMENT_EN_COURS'}
                  className={`px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                    container?.status === 'DEDOUANEMENT_EN_COURS' ?'bg-orange-100 text-orange-800 cursor-not-allowed' :'bg-white border-2 border-orange-600 text-orange-600 hover:bg-orange-50'
                  }`}
                >
                  <Icon name="FileCheck" size={20} className="mx-auto mb-1" />
                  <span className="block text-xs">En phase de dédouanement</span>
                </button>

                <button
                  onClick={() => handleStatusChange('TRANSFERT_A_ENTREPOT')}
                  disabled={container?.status === 'TRANSFERT_A_ENTREPOT'}
                  className={`px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                    container?.status === 'TRANSFERT_A_ENTREPOT' ?'bg-emerald-100 text-emerald-800 cursor-not-allowed' :'bg-white border-2 border-emerald-600 text-emerald-600 hover:bg-emerald-50'
                  }`}
                >
                  <Icon name="Warehouse" size={20} className="mx-auto mb-1" />
                  <span className="block text-xs">À l'entrepôt à Abidjan</span>
                </button>

                <button
                  onClick={() => handleStatusChange('DISTRIBUTION_EN_COURS')}
                  disabled={container?.status === 'DISTRIBUTION_EN_COURS'}
                  className={`px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                    container?.status === 'DISTRIBUTION_EN_COURS' ?'bg-teal-100 text-teal-800 cursor-not-allowed' :'bg-white border-2 border-teal-600 text-teal-600 hover:bg-teal-50'
                  }`}
                >
                  <Icon name="Truck" size={20} className="mx-auto mb-1" />
                  <span className="block text-xs">Distribution en cours</span>
                </button>

                <button
                  onClick={() => handleStatusChange('CONTENEUR_TERMINE')}
                  disabled={container?.status === 'CONTENEUR_TERMINE'}
                  className={`px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                    container?.status === 'CONTENEUR_TERMINE' ?'bg-green-600 text-white cursor-not-allowed' :'bg-white border-2 border-green-600 text-green-600 hover:bg-green-50'
                  }`}
                >
                  <Icon name="CheckCircle2" size={20} className="mx-auto mb-1" />
                  <span className="block text-xs">Conteneur terminé</span>
                </button>
              </div>
              <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                <div className="flex items-start space-x-3">
                  <Icon name="Info" size={20} className="text-blue-600 mt-0.5" />
                  <div className="text-sm text-blue-800">
                    <p className="font-medium mb-1">Cycle de vie du conteneur (7 statuts unifiés)</p>
                    <p>Cliquez sur un statut pour mettre à jour l'état actuel du conteneur. Le statut actif est indiqué par un fond coloré. Ces statuts sont synchronisés avec n8n pour les notifications WhatsApp.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Statistics */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
              <div className="bg-white rounded-lg shadow-sm p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Total envois</p>
                    <p className="text-3xl font-bold text-gray-900 mt-2">{container?.totalShipments}</p>
                  </div>
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Icon name="Package" size={24} className="text-blue-600" />
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-lg shadow-sm p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Total colis</p>
                    <p className="text-3xl font-bold text-gray-900 mt-2">{totalPackages}</p>
                  </div>
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                    <Icon name="Package" size={24} className="text-green-600" />
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-lg shadow-sm p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Chiffre d'affaires</p>
                    {loadingRevenue ? (
                      <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-green-600 mt-2"></div>
                    ) : (
                      <p className="text-3xl font-bold text-green-900 mt-2">
                        {revenue?.totalRevenue?.toFixed(2) || '0.00'}€
                      </p>
                    )}
                    {revenue && (
                      <p className="text-xs text-gray-500 mt-1">
                        Impayé: {revenue?.outstandingAmount?.toFixed(2) || '0.00'}€
                      </p>
                    )}
                  </div>
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                    <Icon name="DollarSign" size={24} className="text-green-600" />
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-lg shadow-sm p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Statut</p>
                    <p className="text-lg font-bold text-gray-900 mt-2">{container?.status}</p>
                  </div>
                  <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                    <Icon name="Info" size={24} className="text-purple-600" />
                  </div>
                </div>
              </div>
            </div>

            {/* ✅ NEW: Documents Section */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-gray-900">
                  Documents du conteneur ({documents?.length || 0})
                </h2>
                <div className="flex items-center space-x-3">
                  <label className="cursor-pointer px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2">
                    <Icon name="Upload" size={20} />
                    <span>{uploadingDocument ? 'Téléchargement...' : 'Télécharger document'}</span>
                    <input
                      type="file"
                      className="hidden"
                      onChange={handleDocumentUpload}
                      disabled={uploadingDocument}
                      accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                    />
                  </label>
                </div>
              </div>

              {loadingDocuments ? (
                <div className="text-center py-8">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
                </div>
              ) : documents?.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {documents?.map((doc) => (
                    <div key={doc?.filePath} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center space-x-2">
                          <Icon name="FileText" size={20} className="text-blue-600" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">{doc?.name}</p>
                            <p className="text-xs text-gray-500">
                              {new Date(doc?.createdAt)?.toLocaleDateString('fr-FR')}
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleDocumentDownload(doc?.filePath, doc?.name)}
                          className="flex-1 px-3 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors flex items-center justify-center space-x-1"
                        >
                          <Icon name="Download" size={16} />
                          <span>Télécharger</span>
                        </button>
                        <button
                          onClick={() => window.open(doc?.fileUrl, '_blank')}
                          className="flex-1 px-3 py-2 bg-green-600 text-white text-sm rounded hover:bg-green-700 transition-colors flex items-center justify-center space-x-1"
                        >
                          <Icon name="Eye" size={16} />
                          <span>Voir</span>
                        </button>
                        <button
                          onClick={() => handleDocumentDelete(doc?.filePath)}
                          className="px-3 py-2 bg-red-600 text-white text-sm rounded hover:bg-red-700 transition-colors"
                        >
                          <Icon name="Trash2" size={16} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Icon name="FileText" size={48} className="mx-auto text-gray-400 mb-4" />
                  <p className="text-gray-500">Aucun document téléchargé</p>
                </div>
              )}
            </div>

            {/* ✅ NEW: Group Messaging Placeholder */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-gray-900">
                  Messagerie groupée (Twilio)
                </h2>
                <div className="px-3 py-1 bg-yellow-100 text-yellow-800 text-sm rounded-full">
                  À venir
                </div>
              </div>
              <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-lg">
                <Icon name="MessageSquare" size={48} className="mx-auto text-gray-400 mb-4" />
                <p className="text-gray-600 mb-2">Envoi de messages groupés via Twilio</p>
                <p className="text-sm text-gray-500">
                  Vous pourrez envoyer des SMS à tous les clients du conteneur lorsque le statut change
                  ou sur demande. Configuration Twilio requise.
                </p>
              </div>
            </div>

            {/* Shipments table */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-gray-900">
                  Envois dans ce conteneur ({shipments?.length || 0})
                </h2>
              </div>

              {shipments?.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Référence
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Expéditeur
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Destinataire
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Statut Paiement
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {shipments?.map((shipment) => (
                        <tr key={shipment?.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-blue-600">
                              {shipment?.reference}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm text-gray-900">{shipment?.senderName}</div>
                            <div className="text-xs text-gray-500">{shipment?.senderCity}</div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm text-gray-900">{shipment?.receiverName}</div>
                            <div className="text-xs text-gray-500">{shipment?.receiverCity}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {getPaymentBadge(shipment?.paymentStatus)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <button
                              onClick={() => navigate(`/shipment-detail-management/${shipment?.id}`)}
                              className="text-green-600 hover:text-green-700 font-medium"
                            >
                              Voir détails
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-12">
                  <p className="text-gray-500">Aucun envoi dans ce conteneur</p>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}